import { ConsumerEventData, ConsumerEventType } from '@upflowy/types';
import { filter } from 'rxjs/operators';

import DataStream from '../helpers/data-stream';
import { QueueSubject } from '../helpers/queue-subject';
import { StateEvent, StateEventData, StateEventMeta } from '../types';

const eventSubject$ = new QueueSubject<StateEvent>();
const eventQueue$ = eventSubject$.pipe(filter((e) => typeof e.type === 'string'));

const location = window.location.toString();
const url = new URL(location);
const origin = url.searchParams.get('origin');
const containerId = url.searchParams.get('containerId');

const stream = origin
  ? new DataStream(window.parent, { origin, containerId: containerId ?? '' })
  : { post: (_message: unknown) => null };

const dispatchEvent = (
  type: ConsumerEventType,
  data?: StateEventData | null,
  meta?: StateEventMeta | null,
) => {
  const event: StateEvent = {
    type,
    data,
    meta,
    secure: {},
    redirectUrl: null,
    source: 'parent',
  };
  eventSubject$.next(event);
};

function onParentWindowMessage(
  message?: MessageEvent<{
    type?: string;
    data?: Record<string, unknown>;
    meta?: StateEventMeta | null;
  }>,
) {
  const dataType = message?.data?.type;
  if (!message || !dataType) return;
  // The following is to filter out redux devtools
  if (dataType === 'ACTION') return;

  const { data, meta } = message?.data;
  const prefixedData = data
    ? Object.entries(data).reduce(
        (p, [key, value]) => ({
          ...p,
          [`user.${key}`]: value,
        }),
        {},
      )
    : {};
  // TODO need to figure out what to do with non ConsumerEventTypes
  dispatchEvent(dataType as ConsumerEventType, prefixedData, meta);
}

// Note: subscribing here to ensure subscription happens before window ready status
// If done in react effect it may miss initial events.
if (window.parent) {
  window.addEventListener('message', onParentWindowMessage);
}

const contextValue = {
  origin,
  eventObserver$: eventQueue$,
  dispatchEvent,
  sendEvent: (event: StateEvent) => {
    eventSubject$.next(event);
  },
  sendParent: (event: ConsumerEventData) => {
    stream.post(event);
  },
};

export const useEventListener = () => contextValue;
